import { Injectable, OnDestroy } from "@angular/core";
import {
  ChannelEnum,
  ChannelRequestViewModel,
  DealerViewModel,
  PartyTypeEnum,
  PartyViewModel,
  SearchDealerRequestModel,
} from "src/domain/client";
import { BrandService } from "./brand.service";
import { ChannelRequestService } from "./channel-request.service";
import { ModelAndDealerService } from "./model-and-dealer.service";
import { SettingService } from "./setting.service";
import { URLService } from "./url.service";
import { URLParamKeys } from "src/domain/constants";
import { ValidTheme } from "src/domain/themes";
import { TrackingService } from "./tracking.service";
import { Subscription } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AppService implements OnDestroy {
  readonly trackingSubscriptions = new Subscription();

  constructor(
    private brandService: BrandService,
    private channelRequestService: ChannelRequestService,
    private modelAndDealerService: ModelAndDealerService,
    private settingService: SettingService,
    private urlService: URLService,
    private trackingService: TrackingService,
  ) {}

  async initiateApplication(
    brandString: ValidTheme,
    dealerNumber = "",
    regNr = "",
  ): Promise<void> {
    this.trackingSubscriptions.add(
      this.modelAndDealerService.selectedDealer$.subscribe(
        this.trackingService.updateDealer.bind(this.trackingService),
      ),
    );
    this.trackingSubscriptions.add(
      this.modelAndDealerService.selectedModel$.subscribe(
        this.trackingService.updateModel.bind(this.trackingService),
      ),
    );
    this.trackingSubscriptions.add(
      this.brandService.brand$.subscribe(
        this.trackingService.updateBrand.bind(this.trackingService),
      ),
    );
    if (!!dealerNumber) {
      const dealerNumbers = dealerNumber.split(",");
      this.modelAndDealerService.setPresetDealers(dealerNumbers);
      dealerNumber = dealerNumbers[0];
    }
    //if brand is cupra we store it as a specialized brand in brandService
    if (brandString == "cupra") {
      this.brandService.setSpecialBrand(brandString);
      brandString = "seat";
    }
    const brand = await this.brandService.getBrand(brandString);
    const [channelRequest, dealers] = await Promise.all([
      this.channelRequestService.initiateChannelRequest(dealerNumber, regNr),
      this.modelAndDealerService.getDealers(
        new SearchDealerRequestModel({
          brandId: brand.brandId,
          channel: ChannelEnum.TBO,
        }),
      ),
      this.settingService.getSettings(),
    ]);
    await this.initiateChannelRequestAndGetDealersSuccessCallback(
      channelRequest,
      dealers,
    );
  }

  //////////

  private async initiateChannelRequestAndGetDealersSuccessCallback(
    channelRequest: ChannelRequestViewModel,
    dealers: DealerViewModel[],
  ): Promise<void> {
    if (!dealers.length) {
      return;
    }

    const dealerParty = channelRequest.parties?.find(
      (_) => _.partyTypeName === PartyTypeEnum.Dealer,
    );
    const selectedDealer =
      dealerParty != null
        ? this.setSelectedDealerAndHandleUrlInput(
            dealers,
            dealerParty,
            channelRequest.shoppingCart?.vehicle?.licensePlate,
          )
        : null;
    this.modelAndDealerService.setSelectedDealer(selectedDealer);
    const { shoppingCart } = channelRequest;
    if (
      shoppingCart?.vehicle?.modelCode != null &&
      shoppingCart.vehicle.year != null
    ) {
      const model = await this.modelAndDealerService.getModelByCodeAndYear(
        shoppingCart.vehicle.modelCode,
        shoppingCart.vehicle.year,
      );
      this.modelAndDealerService.setSelectedModel(model);
    }
  }

  private setSelectedDealerAndHandleUrlInput(
    allDealers: DealerViewModel[],
    dealerParty: PartyViewModel,
    previousRegNr: string | undefined,
  ): DealerViewModel | null {
    let dealerNumberFromUrl = this.urlService.getQueryParamByKey(
      URLParamKeys.dealerNumber,
    );

    if (dealerNumberFromUrl.includes(",")) {
      const selectedDealers = dealerNumberFromUrl.split(",");
      dealerNumberFromUrl = selectedDealers[0];
    }
    const regNrFromUrl = this.urlService.getQueryParamByKey(URLParamKeys.regNr);

    let selectedDealer = allDealers.find(
      (d) => d.number === dealerParty?.identity,
    );

    if (!!dealerNumberFromUrl) {
      if (
        dealerParty?.identity &&
        dealerParty?.identity === dealerNumberFromUrl
      ) {
        selectedDealer = allDealers.find(
          (d) => d.number === dealerParty?.identity,
        );
      } else if (
        !dealerParty?.identity ||
        dealerParty?.identity !== dealerNumberFromUrl
      ) {
        selectedDealer = allDealers.find(
          (d) => d.number === dealerNumberFromUrl,
        );
        this.channelRequestService.updateChannelRequest(
          undefined,
          selectedDealer,
          undefined,
          undefined,
          false,
        );
      }
    }

    if (!!regNrFromUrl && regNrFromUrl !== previousRegNr) {
      this.channelRequestService.updateChannelRequest(
        regNrFromUrl,
        selectedDealer,
        undefined,
        undefined,
        false,
      );
    }

    this.channelRequestService.createChannelRequest(selectedDealer?.number, regNrFromUrl);

    return selectedDealer ?? null;
  }

  ngOnDestroy() {
    this.trackingSubscriptions.unsubscribe();
  }
}
